/**
 * Money is a STRING everywhere. `numeric(15,2)` comes back from pg as a string
 * and that is exactly the Decimal(15,2) FINA wants — see §4.1. Never parseFloat.
 */
/** True for a canonical Decimal(15,2): optional sign, digits, dot, exactly 2 decimals. */
export function isDecimal2(value) {
    return /^-?\d{1,13}\.\d{2}$/.test(value);
}
/**
 * Normalises to exactly 2 decimals with a dot separator, without going through
 * a float. Accepts "5", "5.1", "5.10", "-5.005" (rejected — see below).
 * Throws rather than silently rounding a value with more precision than cents.
 */
export function toDecimal2(value) {
    const trimmed = value.trim().replace(",", ".");
    const m = /^(-?)(\d+)(?:\.(\d*))?$/.exec(trimmed);
    if (!m)
        throw new Error(`Not a decimal amount: "${value}"`);
    const sign = m[1] ?? "";
    const whole = m[2] ?? "0";
    const frac = m[3] ?? "";
    if (frac.length > 2) {
        throw new Error(`Amount "${value}" has sub-cent precision; refusing to round silently`);
    }
    return `${sign}${whole}.${frac.padEnd(2, "0")}`;
}
/** Adds two Decimal(15,2) strings exactly, via integer cents. */
export function addDecimal2(a, b) {
    return centsToDecimal2(decimal2ToCents(a) + decimal2ToCents(b));
}
/** `a - b`, in cents, so nothing drifts. Negative results are ordinary here. */
export function subtractDecimal2(a, b) {
    return centsToDecimal2(decimal2ToCents(a) - decimal2ToCents(b));
}
export function decimal2ToCents(value) {
    const normalised = toDecimal2(value);
    const negative = normalised.startsWith("-");
    const digits = normalised.replace("-", "").replace(".", "");
    const cents = BigInt(digits);
    return negative ? -cents : cents;
}
export function centsToDecimal2(cents) {
    const negative = cents < 0n;
    const abs = negative ? -cents : cents;
    const whole = abs / 100n;
    const frac = abs % 100n;
    return `${negative ? "-" : ""}${whole}.${frac.toString().padStart(2, "0")}`;
}
/**
 * QR amount format (§5.8): euros AND cents, no separators, no leading zeros.
 *   €1,525.99 -> "152599"      €-105.50 -> "-10550"
 */
export function toQrAmount(value) {
    const cents = decimal2ToCents(value);
    return cents.toString();
}
//# sourceMappingURL=money.js.map